import { NODE_TYPE_LABELS, nodeDisplayColor, resolveNodeType } from '../../lib/nodeConfig';
import { formatRouteLength } from '../../lib/calibration';
import {
  buildElementsSectionsByGroup,
  buildElementsSectionsByType,
  type ElementsViewMode,
} from '../../lib/elementsListGrouping';
import { effectiveInputRateLabel } from '../../lib/projectNormalization';
import { buildNodeConnections } from '../../lib/nodeConnections';
import { isNodeInAnyGroup } from '../../lib/nodeGroups';
import { computeRouteLengthMeters } from '../../lib/routeGeometry';
import NodeGroupsPanel from './NodeGroupsPanel';
import { useEditorStore } from '../../store/editorStore';
import type { NodeType, ProjectDocument, ProjectNode, UnitSystem } from '../../types/project';
import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';

const STORAGE_KEY_ORDER = 'sim-designer-panel-section-order';
const STORAGE_KEY_SPLIT = 'sim-designer-panel-top-ratio';

type SectionId = 'elements' | 'properties';

const DEFAULT_ORDER: SectionId[] = ['elements', 'properties'];
const MIN_SECTION_RATIO = 0.18;

function nodeSelectionHighlightClass(
  document: ProjectDocument,
  nodeId: string,
  isActive: boolean,
  focusedGroupId: string | null,
): string {
  if (!isActive) {
    return 'text-text-muted hover:bg-surface-overlay hover:text-text';
  }
  if (focusedGroupId) return 'selection-highlight-item-ingroup';
  if (isNodeInAnyGroup(document, nodeId)) return 'selection-highlight-item-grouped';
  return 'selection-highlight-item';
}
const MAX_SECTION_RATIO = 0.82;

function loadSectionOrder(): SectionId[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_ORDER);
    if (!raw) return DEFAULT_ORDER;
    const parsed = JSON.parse(raw) as unknown;
    if (
      Array.isArray(parsed) &&
      parsed.length === 2 &&
      parsed.includes('elements') &&
      parsed.includes('properties')
    ) {
      return parsed as SectionId[];
    }
  } catch {
    /* ignore */
  }
  return DEFAULT_ORDER;
}

function loadTopRatio(): number {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_SPLIT);
    const value = raw ? Number.parseFloat(raw) : 0.38;
    if (!Number.isFinite(value)) return 0.38;
    return Math.min(MAX_SECTION_RATIO, Math.max(MIN_SECTION_RATIO, value));
  } catch {
    return 0.38;
  }
}

function clampRatio(ratio: number): number {
  return Math.min(MAX_SECTION_RATIO, Math.max(MIN_SECTION_RATIO, ratio));
}

function nodeMatchesFilter(node: ProjectNode, query: string): boolean {
  const name =
    typeof node.name === 'string' ? node.name : String(node.name ?? '');
  const type = resolveNodeType(node);
  return (
    name.toLowerCase().includes(query) || type.toLowerCase().includes(query)
  );
}

const EMPTY_NODES: ProjectNode[] = [];

// ------------------------------------------------------------
// Drag handle icon
// ------------------------------------------------------------

function GripIcon() {
  return (
    <svg
      className="h-3.5 w-3.5 shrink-0 text-text-subtle"
      viewBox="0 0 16 16"
      fill="currentColor"
      aria-hidden
    >
      <circle cx="5" cy="4" r="1.2" />
      <circle cx="11" cy="4" r="1.2" />
      <circle cx="5" cy="8" r="1.2" />
      <circle cx="11" cy="8" r="1.2" />
      <circle cx="5" cy="12" r="1.2" />
      <circle cx="11" cy="12" r="1.2" />
    </svg>
  );
}

// ------------------------------------------------------------
// Elements list — tabs, collapsible groups, type/group views
// ------------------------------------------------------------

function ChevronIcon({ collapsed }: { collapsed: boolean }) {
  return (
    <svg
      className={`h-3 w-3 shrink-0 text-text-subtle transition-transform ${
        collapsed ? '' : 'rotate-90'
      }`}
      viewBox="0 0 16 16"
      fill="currentColor"
      aria-hidden
    >
      <path d="M6 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" fill="none" />
    </svg>
  );
}

interface ElementsSectionPanelProps {
  id: string;
  label: string;
  count: number;
  collapsed: boolean;
  onToggle: () => void;
  children: ReactNode;
}

function ElementsSectionPanel({
  id,
  label,
  count,
  collapsed,
  onToggle,
  children,
}: ElementsSectionPanelProps) {
  return (
    <div className="border-b border-border-subtle/70 last:border-b-0">
      <button
        type="button"
        id={`elements-section-${id}`}
        aria-expanded={!collapsed}
        onClick={onToggle}
        className="flex w-full items-center gap-1.5 px-2.5 py-1.5 text-left transition hover:bg-surface-overlay"
      >
        <ChevronIcon collapsed={collapsed} />
        <span className="min-w-0 flex-1 truncate text-[10px] font-medium uppercase tracking-wide text-text-subtle">
          {label}
        </span>
        <span className="shrink-0 text-[10px] text-text-subtle">{count}</span>
      </button>
      {!collapsed && <div className="pb-1">{children}</div>}
    </div>
  );
}

export const ElementsList = memo(function ElementsList() {
  const projectDocument = useEditorStore((s) => s.document);
  const nodes = useEditorStore((s) => s.document?.nodes ?? EMPTY_NODES);
  const routes = useEditorStore((s) => s.document?.routes ?? []);
  const selectedNodeIds = useEditorStore((s) => s.selectedNodeIds);
  const selectedRouteIds = useEditorStore((s) => s.selectedRouteIds);
  const selectedRouteIdSet = useMemo(
    () => new Set(selectedRouteIds),
    [selectedRouteIds],
  );
  const focusedGroupId = useEditorStore((s) => s.focusedGroupId);
  const permission = useEditorStore((s) => s.permission);
  const selectNode = useEditorStore((s) => s.selectNode);
  const selectRoute = useEditorStore((s) => s.selectRoute);
  const openNodeEdit = useEditorStore((s) => s.openNodeEdit);
  const readOnly = permission !== 'edit';
  const [filter, setFilter] = useState('');
  const [viewMode, setViewMode] = useState<ElementsViewMode>('type');
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(
    () => new Set(),
  );
  const selectedNodeIdSet = useMemo(
    () => new Set(selectedNodeIds),
    [selectedNodeIds],
  );
  const nodesById = useMemo(
    () => new Map(nodes.map((node) => [node.id, node])),
    [nodes],
  );
  const pixelsPerMeter = projectDocument?.settings?.pixels_per_meter ?? 0;
  const unit: UnitSystem = projectDocument?.settings?.unit_system ?? 'metric_m';

  const routeLengthLabel = useCallback(
    (routeId: string) => {
      const route = routes.find((r) => r.id === routeId);
      if (!route) return null;
      const meters = computeRouteLengthMeters(route, nodesById, pixelsPerMeter);
      if (meters === null) return null;
      return formatRouteLength(meters, unit);
    },
    [routes, nodesById, pixelsPerMeter, unit],
  );

  const filteredNodes = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return nodes;
    return nodes.filter((n) => nodeMatchesFilter(n, q));
  }, [nodes, filter]);

  const filteredRoutes = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return routes;
    return routes.filter((r) => r.name.toLowerCase().includes(q));
  }, [routes, filter]);

  const sections = useMemo(() => {
    if (viewMode === 'group' && projectDocument) {
      return buildElementsSectionsByGroup(
        projectDocument,
        filteredNodes,
        filteredRoutes,
      );
    }
    return buildElementsSectionsByType(filteredNodes, filteredRoutes);
  }, [viewMode, projectDocument, filteredNodes, filteredRoutes]);

  const sectionIds = useMemo(
    () => sections.map((section) => section.id),
    [sections],
  );

  useEffect(() => {
    setCollapsedSections(new Set());
  }, [viewMode, filter]);

  const toggleSection = useCallback((sectionId: string) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) next.delete(sectionId);
      else next.add(sectionId);
      return next;
    });
  }, []);

  const expandAllSections = useCallback(() => {
    setCollapsedSections(new Set());
  }, []);

  const collapseAllSections = useCallback(() => {
    setCollapsedSections(new Set(sectionIds));
  }, [sectionIds]);

  useEffect(() => {
    const hasSelection =
      selectedRouteIds.length > 0 || selectedNodeIds.length > 0;
    if (!hasSelection) return;

    const q = filter.trim();
    if (q) {
      const routeVisible =
        selectedRouteIds.length === 0 ||
        selectedRouteIds.some((id) =>
          filteredRoutes.some((route) => route.id === id),
        );
      const nodesVisible =
        selectedNodeIds.length === 0 ||
        selectedNodeIds.some((id) =>
          filteredNodes.some((node) => node.id === id),
        );
      if (!routeVisible || !nodesVisible) {
        setFilter('');
        return;
      }
    }

    if (selectedRouteIds.length > 0) {
      document
        .getElementById(`elements-route-${selectedRouteIds[0]}`)
        ?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      return;
    }

    const visibleNode = filteredNodes.find((node) =>
      selectedNodeIdSet.has(node.id),
    );
    const targetId =
      visibleNode?.id ?? selectedNodeIds[selectedNodeIds.length - 1];
    document
      .getElementById(`elements-node-${targetId}`)
      ?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [
    filter,
    selectedRouteIds,
    selectedNodeIds,
    filteredNodes,
    filteredRoutes,
    selectedNodeIdSet,
  ]);

  if (nodes.length === 0 && routes.length === 0) {
    return (
      <p className="px-4 py-6 text-center text-xs text-text-subtle">
        Add nodes from the palette
      </p>
    );
  }

  const showFilter = nodes.length > 40 || routes.length > 40;

  return (
    <>
      <div className="border-b border-border-subtle px-2 py-2">
        <div className="mb-2 grid grid-cols-3 gap-0.5 rounded-lg border border-border bg-surface p-0.5">
          <button
            type="button"
            onClick={() => setViewMode('type')}
            className={`rounded-md px-2 py-1 text-[10px] font-medium transition ${
              viewMode === 'type'
                ? 'bg-surface-overlay text-text'
                : 'text-text-muted hover:text-text'
            }`}
          >
            By type
          </button>
          <button
            type="button"
            onClick={() => setViewMode('group')}
            className={`rounded-md px-2 py-1 text-[10px] font-medium transition ${
              viewMode === 'group'
                ? 'bg-surface-overlay text-text'
                : 'text-text-muted hover:text-text'
            }`}
          >
            By group
          </button>
          <button
            type="button"
            onClick={() => setViewMode('manage')}
            className={`rounded-md px-2 py-1 text-[10px] font-medium transition ${
              viewMode === 'manage'
                ? 'bg-surface-overlay text-text'
                : 'text-text-muted hover:text-text'
            }`}
          >
            Groups
          </button>
        </div>
        {viewMode !== 'manage' && (
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={expandAllSections}
              className="btn-ghost flex-1 px-2 py-1 text-[10px]"
            >
              Expand all
            </button>
            <button
              type="button"
              onClick={collapseAllSections}
              className="btn-ghost flex-1 px-2 py-1 text-[10px]"
            >
              Collapse all
            </button>
          </div>
        )}
      </div>

      {viewMode === 'manage' ? (
        <NodeGroupsPanel />
      ) : (
        <>
      {showFilter && (
        <div className="border-b border-border-subtle px-2 py-2">
          <input
            type="search"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter elements…"
            className="input-field w-full py-1.5 text-xs"
          />
        </div>
      )}

      {sections.length > 0 ? (
        <div className="pb-1">
          {sections.map((section) => {
            const count = section.nodes.length + section.routes.length;
            const collapsed = collapsedSections.has(section.id);
            return (
              <ElementsSectionPanel
                key={section.id}
                id={section.id}
                label={section.label}
                count={count}
                collapsed={collapsed}
                onToggle={() => toggleSection(section.id)}
              >
                {section.isEmptyGroup && section.staleNodeIds?.length === 0 && (
                  <p className="px-3 pb-2 text-xs text-text-subtle">
                    This group has no nodes yet. Use the Groups tab to add members.
                  </p>
                )}
                {section.staleNodeIds && section.staleNodeIds.length > 0 && (
                  <div className="mx-1.5 mb-1 rounded-md border border-warning/30 bg-warning-muted/10 px-2 py-1.5 text-[10px] text-warning-fg">
                    {section.staleNodeIds.length} deleted node reference
                    {section.staleNodeIds.length === 1 ? '' : 's'} — fix in Groups
                    tab
                  </div>
                )}
                {section.routes.length > 0 && (
                  <ul className="space-y-0.5 px-1.5">
                    {section.routes.map((route) => {
                      const isActive = selectedRouteIdSet.has(route.id);
                      const lengthLabel = routeLengthLabel(route.id);
                      return (
                        <li key={route.id}>
                          <button
                            id={`elements-route-${route.id}`}
                            type="button"
                            onClick={() => selectRoute(route.id, { focus: true })}
                            aria-current={isActive ? 'true' : undefined}
                            className={`flex w-full items-center gap-1.5 rounded-md px-2 py-1 text-left text-xs leading-tight transition ${
                              isActive
                                ? 'selection-highlight-item'
                                : 'text-text-muted hover:bg-surface-overlay hover:text-text'
                            }`}
                          >
                            <span
                              className="h-0.5 w-3.5 shrink-0 rounded-full"
                              style={{
                                backgroundColor: route.color ?? '#878786',
                              }}
                            />
                            <span className="min-w-0 flex-1 truncate">
                              {route.name}
                              {lengthLabel && (
                                <span className="ml-1 text-[10px] text-text-subtle">
                                  · {lengthLabel}
                                </span>
                              )}
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
                {section.nodes.length > 0 && (
                  <ul className="space-y-0.5 px-1.5 pt-0.5">
                    {section.nodes.map((node) => {
                      const isActive = selectedNodeIdSet.has(node.id);
                      return (
                        <li key={node.id}>
                          <button
                            id={`elements-node-${node.id}`}
                            type="button"
                            onClick={(e) =>
                              selectNode(node.id, {
                                focus: true,
                                additive: e.shiftKey,
                                subtractive: e.ctrlKey || e.metaKey,
                              })
                            }
                            onDoubleClick={(e) => {
                              if (readOnly) return;
                              e.preventDefault();
                              openNodeEdit(node.id);
                            }}
                            title={
                              readOnly
                                ? node.name
                                : `${node.name}\n\nDouble-click to edit`
                            }
                            aria-current={isActive ? 'true' : undefined}
                            className={`flex w-full items-center gap-1.5 rounded-md px-2 py-1 text-left text-xs leading-tight transition ${
                              projectDocument
                                ? nodeSelectionHighlightClass(
                                    projectDocument,
                                    node.id,
                                    isActive,
                                    focusedGroupId,
                                  )
                                : isActive
                                  ? 'selection-highlight-item'
                                  : 'text-text-muted hover:bg-surface-overlay hover:text-text'
                            }`}
                          >
                            <span
                              className="h-2 w-2 shrink-0 rounded-full ring-1 ring-white/15"
                              style={{
                                backgroundColor: nodeDisplayColor(
                                  node,
                                  projectDocument?.settings,
                                ),
                              }}
                            />
                            <span className="min-w-0 flex-1 truncate">
                              {node.name}
                            </span>
                            {viewMode === 'type' ? (
                              <span className="shrink-0 text-[9px] uppercase text-text-subtle">
                                {NODE_TYPE_LABELS[
                                  resolveNodeType(node) as NodeType
                                ]?.slice(0, 4)}
                              </span>
                            ) : null}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </ElementsSectionPanel>
            );
          })}
        </div>
      ) : (
        <p className="px-4 py-4 text-center text-xs text-text-subtle">
          No elements match your filter
        </p>
      )}
        </>
      )}
    </>
  );
});

// ------------------------------------------------------------
// Properties form
// ------------------------------------------------------------

export function PropertiesContent() {
  const selectedNodeIds = useEditorStore((s) => s.selectedNodeIds);
  const selectedRouteIds = useEditorStore((s) => s.selectedRouteIds);
  const document = useEditorStore((s) => s.document);
  const permission = useEditorStore((s) => s.permission);
  const updateNode = useEditorStore((s) => s.updateNode);
  const updateRoute = useEditorStore((s) => s.updateRoute);
  const deleteSelected = useEditorStore((s) => s.deleteSelected);
  const openNodeEdit = useEditorStore((s) => s.openNodeEdit);
  const readOnly = permission !== 'edit';

  const nodes = document?.nodes ?? EMPTY_NODES;
  const nodesById = useMemo(
    () => new Map(nodes.map((n) => [n.id, n])),
    [nodes],
  );

  const selectedNode =
    selectedNodeIds.length === 1
      ? nodes.find((n) => n.id === selectedNodeIds[0]) ?? null
      : null;

  const selectedRoute =
    selectedRouteIds.length === 1 && selectedNodeIds.length === 0
      ? document?.routes.find((r) => r.id === selectedRouteIds[0])
      : null;

  const unit: UnitSystem = document?.settings?.unit_system ?? 'metric_m';
  const pixelsPerMeter = document?.settings?.pixels_per_meter ?? 0;
  const selectedRouteLengthMeters = useMemo(() => {
    if (!selectedRoute) return null;
    return computeRouteLengthMeters(selectedRoute, nodesById, pixelsPerMeter);
  }, [selectedRoute, nodesById, pixelsPerMeter]);

  const selectedRouteTravelSeconds = useMemo(() => {
    if (!selectedRoute || selectedRouteLengthMeters === null) return null;
    const length =
      selectedRouteLengthMeters > 0
        ? selectedRouteLengthMeters
        : (selectedRoute.length_meters ?? 0);
    const speed = selectedRoute.speed_m_per_min ?? 1;
    if (length <= 0 || speed <= 0) return null;
    return (length / speed) * 60;
  }, [selectedRoute, selectedRouteLengthMeters]);

  return (
    <div className="p-4 text-sm">
      {!selectedNode && !selectedRoute && selectedNodeIds.length === 0 && selectedRouteIds.length === 0 && (
        <div className="rounded-lg border border-dashed border-border bg-surface/50 px-4 py-8 text-center">
          <p className="text-text-muted">Nothing selected</p>
          <p className="mt-1 text-xs text-text-subtle">
            Click a node or route on the canvas, or pick from the list above.
          </p>
        </div>
      )}

      {selectedNodeIds.length > 1 && !selectedRoute && selectedRouteIds.length === 0 && (
        <div className="space-y-4">
          <div>
            <p className="font-medium text-text">
              {selectedNodeIds.length} nodes selected
            </p>
            <p className="mt-1 text-xs text-text-subtle">
              Drag to move together · Del to delete · Ctrl+C / Ctrl+V to copy and
              paste
            </p>
          </div>
          {!readOnly && (
            <button
              type="button"
              onClick={() => deleteSelected()}
              className="btn-danger-ghost w-full"
            >
              Delete {selectedNodeIds.length} nodes
            </button>
          )}
        </div>
      )}

      {selectedRouteIds.length > 1 && selectedNodeIds.length === 0 && !selectedRoute && (
        <div className="space-y-4">
          <div>
            <p className="font-medium text-text">
              {selectedRouteIds.length} routes selected
            </p>
            <p className="mt-1 text-xs text-text-subtle">
              Del to delete selected routes
            </p>
          </div>
          {!readOnly && (
            <button
              type="button"
              onClick={() => deleteSelected()}
              className="btn-danger-ghost w-full"
            >
              Delete {selectedRouteIds.length} routes
            </button>
          )}
        </div>
      )}

      {selectedNodeIds.length > 0 && selectedRouteIds.length > 0 && (
        <div className="space-y-4">
          <div>
            <p className="font-medium text-text">
              {selectedNodeIds.length} nodes and {selectedRouteIds.length} routes
              selected
            </p>
            <p className="mt-1 text-xs text-text-subtle">
              Del to delete the current selection
            </p>
          </div>
          {!readOnly && (
            <button
              type="button"
              onClick={() => deleteSelected()}
              className="btn-danger-ghost w-full"
            >
              Delete selection
            </button>
          )}
        </div>
      )}

      {selectedNode && (
        <div className="space-y-4">
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="font-medium text-text">{selectedNode.name}</p>
              <p className="text-xs text-text-subtle">
                {NODE_TYPE_LABELS[resolveNodeType(selectedNode) as NodeType] ??
                  resolveNodeType(selectedNode)}
              </p>
            </div>
            {!readOnly && (
              <button
                type="button"
                className="btn-secondary shrink-0 px-2 py-1 text-xs"
                onClick={() => openNodeEdit(selectedNode.id)}
              >
                Edit…
              </button>
            )}
          </div>

          {resolveNodeType(selectedNode) === 'input' && (
            <p className="text-xs text-text-muted">
              Rate: {effectiveInputRateLabel(selectedNode as Record<string, unknown>)}
            </p>
          )}

          {document && (
            <div className="text-xs text-text-subtle">
              {(() => {
                const conn = buildNodeConnections(document, selectedNode.id);
                return (
                  <>
                    <p>{conn.incoming.length} incoming · {conn.outgoing.length} outgoing</p>
                  </>
                );
              })()}
            </div>
          )}

          <div>
            <label className="mb-1 block text-xs text-text-muted">Name</label>
            <input
              type="text"
              value={selectedNode.name}
              disabled={readOnly}
              onChange={(e) =>
                updateNode(selectedNode.id, { name: e.target.value })
              }
              className="input-field w-full"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-text-muted">Type</label>
            <p className="text-text">
              {NODE_TYPE_LABELS[resolveNodeType(selectedNode) as NodeType] ??
                (resolveNodeType(selectedNode) || 'Unknown')}
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs text-text-muted">X</label>
              <input
                type="number"
                value={Math.round(selectedNode.x)}
                disabled={readOnly}
                onChange={(e) =>
                  updateNode(selectedNode.id, {
                    x: Number(e.target.value),
                  })
                }
                className="input-field w-full"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs text-text-muted">Y</label>
              <input
                type="number"
                value={Math.round(selectedNode.y)}
                disabled={readOnly}
                onChange={(e) =>
                  updateNode(selectedNode.id, {
                    y: Number(e.target.value),
                  })
                }
                className="input-field w-full"
              />
            </div>
          </div>
          {!readOnly && (
            <button
              type="button"
              onClick={() => deleteSelected()}
              className="btn-danger-ghost w-full"
            >
              Delete node
            </button>
          )}
        </div>
      )}

      {selectedRoute && !selectedNode && (
        <div className="space-y-4">
          <div>
            <p className="font-medium text-text">{selectedRoute.name}</p>
            <p className="mt-1 text-xs text-text-subtle">
              {nodesById.get(selectedRoute.from_node_id)?.name ?? '?'} →{' '}
              {nodesById.get(selectedRoute.to_node_id)?.name ?? '?'}
            </p>
          </div>
          <div className="rounded-lg border border-border bg-surface/40 p-3">
            <p className="mb-1 text-[10px] font-medium uppercase tracking-wide text-text-subtle">
              Conveyor / route physics
            </p>
            {selectedRouteLengthMeters === null ? (
              <p className="text-xs text-text-muted">
                Calibrate layout scale (Layout panel) to derive route length from
                the drawing. Until then, simulation uses a 10 m fallback.
              </p>
            ) : (
              <>
                <p className="text-sm font-medium text-text">
                  Length: {formatRouteLength(selectedRouteLengthMeters, unit)}
                </p>
                {selectedRoute.length_meters != null &&
                  selectedRoute.length_meters > 0 && (
                    <p className="mt-0.5 text-[10px] text-text-subtle">
                      Stored for simulation:{' '}
                      {formatRouteLength(selectedRoute.length_meters, unit)}
                    </p>
                  )}
                {selectedRouteTravelSeconds !== null && (
                  <p className="mt-1 text-xs text-text-subtle">
                    Travel time at {selectedRoute.speed_m_per_min ?? 1} m/min:{' '}
                    {selectedRouteTravelSeconds >= 60
                      ? `${(selectedRouteTravelSeconds / 60).toFixed(1)} min`
                      : `${selectedRouteTravelSeconds.toFixed(0)} s`}
                  </p>
                )}
              </>
            )}
          </div>
          <div>
            <label className="mb-1 block text-xs text-text-muted">
              Belt speed (m/min)
            </label>
            <input
              type="number"
              min={0.1}
              step={0.1}
              value={selectedRoute.speed_m_per_min ?? 1}
              disabled={readOnly}
              onChange={(e) =>
                updateRoute(selectedRoute.id, {
                  speed_m_per_min: Number(e.target.value),
                })
              }
              className="input-field w-full"
            />
            <p className="mt-1 text-[10px] text-text-subtle">
              Conveyor linear speed along this route. Combined with length, this
              sets how long each product spends in transit.
            </p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs text-text-muted">
                Max items on belt
              </label>
              <input
                type="number"
                min={0}
                step={1}
                value={selectedRoute.max_items_in_transit ?? 0}
                disabled={readOnly}
                onChange={(e) =>
                  updateRoute(selectedRoute.id, {
                    max_items_in_transit: Number(e.target.value),
                  })
                }
                className="input-field w-full"
                placeholder="0 = unlimited"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs text-text-muted">
                Max load (kg)
              </label>
              <input
                type="number"
                min={0}
                step={1}
                value={selectedRoute.max_load_kg ?? 0}
                disabled={readOnly}
                onChange={(e) =>
                  updateRoute(selectedRoute.id, {
                    max_load_kg: Number(e.target.value),
                  })
                }
                className="input-field w-full"
                placeholder="0 = unlimited"
              />
            </div>
          </div>
          <p className="text-[10px] text-text-subtle">
            When a belt limit is reached, new products wait at the entry until
            space opens. Set product weights on input nodes for kg limits.
          </p>
          {!readOnly && (
            <button
              type="button"
              onClick={() => deleteSelected()}
              className="btn-danger-ghost w-full"
            >
              Delete route
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------
// Resizable / reorderable section shell
// ------------------------------------------------------------

interface PanelSectionProps {
  id: SectionId;
  title: string;
  subtitle?: ReactNode;
  style: CSSProperties;
  isDragging: boolean;
  isDragOver: boolean;
  onDragStart: (id: SectionId) => void;
  onDragEnd: () => void;
  onDragOver: (id: SectionId) => void;
  onDrop: (id: SectionId) => void;
  children: ReactNode;
}

function PanelSection({
  id,
  title,
  subtitle,
  style,
  isDragging,
  isDragOver,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDrop,
  children,
}: PanelSectionProps) {
  return (
    <section
      className={`flex min-h-0 flex-col overflow-hidden border-border transition-opacity ${
        isDragging ? 'opacity-50' : 'opacity-100'
      } ${isDragOver ? 'ring-2 ring-inset ring-accent/40' : ''}`}
      style={style}
      onDragOver={(e) => {
        e.preventDefault();
        onDragOver(id);
      }}
      onDrop={(e) => {
        e.preventDefault();
        onDrop(id);
      }}
    >
      <div
        draggable
        onDragStart={(e) => {
          e.dataTransfer.effectAllowed = 'move';
          e.dataTransfer.setData('text/plain', id);
          onDragStart(id);
        }}
        onDragEnd={onDragEnd}
        className="flex shrink-0 cursor-grab items-start gap-2 border-b border-border px-3 py-3 active:cursor-grabbing"
        title="Drag to reorder sections"
      >
        <GripIcon />
        <div className="min-w-0 flex-1 select-none">
          <h2 className="panel-section-title">{title}</h2>
          {subtitle}
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
    </section>
  );
}

function ResizeDivider({ onPointerDown }: { onPointerDown: (e: React.PointerEvent) => void }) {
  return (
    <div
      role="separator"
      aria-orientation="horizontal"
      aria-label="Resize panel sections"
      className="group flex h-2 shrink-0 cursor-row-resize items-center justify-center bg-surface-raised hover:bg-accent/10"
      onPointerDown={onPointerDown}
      title="Drag to resize sections"
    >
      <div className="h-0.5 w-12 rounded-full bg-border transition group-hover:bg-accent/50" />
    </div>
  );
}

// ------------------------------------------------------------
// Right sidebar: Elements + Properties
// ------------------------------------------------------------

export default function PropertiesPanel() {
  const document = useEditorStore((s) => s.document);
  const nodes = document?.nodes ?? EMPTY_NODES;

  const panelRef = useRef<HTMLElement>(null);
  const [sectionOrder, setSectionOrder] = useState<SectionId[]>(loadSectionOrder);
  const [topRatio, setTopRatio] = useState(loadTopRatio);
  const [draggingId, setDraggingId] = useState<SectionId | null>(null);
  const [dragOverId, setDragOverId] = useState<SectionId | null>(null);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_ORDER, JSON.stringify(sectionOrder));
  }, [sectionOrder]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_SPLIT, String(topRatio));
  }, [topRatio]);

  const swapSections = useCallback((targetId: SectionId) => {
    if (!draggingId || draggingId === targetId) return;
    setSectionOrder((order) => [...order].reverse());
    setDraggingId(null);
    setDragOverId(null);
  }, [draggingId]);

  const handleResizePointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      const panel = panelRef.current;
      if (!panel) return;

      const startY = e.clientY;
      const startRatio = topRatio;
      const panelHeight = panel.clientHeight;
      const ratioRef = { current: startRatio };

      const onMove = (ev: PointerEvent) => {
        const delta = ev.clientY - startY;
        const next = clampRatio(startRatio + delta / panelHeight);
        ratioRef.current = next;
        setTopRatio(next);
      };

      const onUp = () => {
        setTopRatio(ratioRef.current);
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
      };

      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
    },
    [topRatio],
  );

  const sectionMeta: Record<
    SectionId,
    { title: string; subtitle?: ReactNode; content: ReactNode }
  > = {
    elements: {
      title: 'Elements',
      subtitle: (
        <p className="mt-0.5 text-[11px] text-text-subtle">
          {nodes.length} node{nodes.length !== 1 ? 's' : ''} ·{' '}
          {document?.routes.length ?? 0} route
          {(document?.routes.length ?? 0) !== 1 ? 's' : ''}
        </p>
      ),
      content: <ElementsList />,
    },
    properties: {
      title: 'Properties',
      content: <PropertiesContent />,
    },
  };

  const [topId, bottomId] = sectionOrder;
  const topStyle: CSSProperties = {
    flex: `${topRatio} ${topRatio} 0`,
    minHeight: 0,
  };
  const bottomStyle: CSSProperties = {
    flex: `${1 - topRatio} ${1 - topRatio} 0`,
    minHeight: 0,
  };

  const renderSection = (id: SectionId, style: CSSProperties) => {
    const meta = sectionMeta[id];
    return (
      <PanelSection
        key={id}
        id={id}
        title={meta.title}
        subtitle={meta.subtitle}
        style={style}
        isDragging={draggingId === id}
        isDragOver={dragOverId === id && draggingId !== id}
        onDragStart={setDraggingId}
        onDragEnd={() => {
          setDraggingId(null);
          setDragOverId(null);
        }}
        onDragOver={setDragOverId}
        onDrop={swapSections}
      >
        {meta.content}
      </PanelSection>
    );
  };

  return (
    <aside
      ref={panelRef}
      className="flex h-full w-72 shrink-0 flex-col border-l border-border bg-surface-raised"
    >
      {renderSection(topId, topStyle)}
      <ResizeDivider onPointerDown={handleResizePointerDown} />
      {renderSection(bottomId, bottomStyle)}
    </aside>
  );
}
